// src/components/admin/PickupRequestsManagement.jsx
import React, { useState, useEffect } from 'react';
import { adminLogisticsService } from '../../services/adminService';

const STATUS_COLORS = {
  pending:   { bg: '#ffc10720', color: '#ffc107' },
  scheduled: { bg: '#17a2b820', color: '#17a2b8' },
  picked_up: { bg: '#6f42c120', color: '#6f42c1' },
  completed: { bg: '#28a74520', color: '#28a745' },
  cancelled: { bg: '#dc354520', color: '#dc3545' },
};

const TIME_SLOTS = [
  '9AM-11AM',
  '10AM-12PM',
  '11AM-1PM',
  '12PM-2PM',
  '2PM-4PM',
  '4PM-6PM',
  '6PM-8PM',
];

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span className="status-badge" style={{ background: s.bg, color: s.color }}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
};

// ─── Assign & Edit Modal ──────────────────────────────────────────────────────
const AssignDriverModal = ({ pickup, drivers, onClose, onSuccess }) => {
  const todayStr = new Date().toISOString().split('T')[0];

  const [form, setForm] = useState({
    assigned_driver_id: pickup.assigned_driver_id ? String(pickup.assigned_driver_id) : '',
    status:             pickup.status || 'scheduled',
    city:               pickup.city || '',
    address:            pickup.address || '',
    preferred_date:     pickup.preferred_date
                          ? new Date(pickup.preferred_date).toISOString().split('T')[0]
                          : '',
    preferred_slot:     pickup.preferred_slot || '',
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async () => {
    if (!form.assigned_driver_id) { alert('Please select a driver'); return; }
    try {
      setSubmitting(true);
      await adminLogisticsService.updatePickupRequest(pickup.id, {
        assigned_driver_id: Number(form.assigned_driver_id),
        status:             form.status,
        city:               form.city    || undefined,
        address:            form.address || undefined,
        preferred_date:     form.preferred_date || undefined,
        preferred_slot:     form.preferred_slot || undefined,
      });
      alert('✅ Pickup request updated successfully!');
      onSuccess();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '11px 14px',
    border: '1.5px solid #e0e0e0', borderRadius: 10,
    fontSize: 14, background: '#fff', outline: 'none',
    boxSizing: 'border-box', color: '#222',
  };

  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 700,
    color: '#888', textTransform: 'uppercase',
    letterSpacing: '0.5px', marginBottom: 7,
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: 580, width: '100%' }}
      >
        {/* Header */}
        <div className="modal-header">
          <h2>🚚 Update Pickup Request</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Summary */}
          <div className="inspection-summary" style={{ marginBottom: 24 }}>
            <h3>Pickup #{pickup.id}</h3>
            <p>📋 Sell Request #{pickup.sell_request_id}</p>
          </div>

          {/* ── Assign Driver ── */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Assign Driver *</label>
            <div style={{ position: 'relative' }}>
              <select
                className="form-select"
                value={form.assigned_driver_id}
                onChange={e => set('assigned_driver_id', e.target.value)}
                style={{ ...inputStyle, appearance: 'none', paddingRight: 36 }}
              >
                <option value="">— Choose a driver —</option>
                {drivers.length === 0 && <option disabled>No drivers found</option>}
                {drivers.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.name}{d.phone ? ` (${d.phone})` : d.email ? ` (${d.email})` : ''}
                  </option>
                ))}
              </select>
              <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#aaa' }}>▾</span>
            </div>
            {drivers.length === 0 && (
              <small style={{ color: '#dc3545', marginTop: 6, display: 'block' }}>
                ⚠️ No drivers found. Ensure users with role "driver" exist in the system.
              </small>
            )}
          </div>

          {/* ── Status ── */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Status</label>
            <div style={{ position: 'relative' }}>
              <select
                value={form.status}
                onChange={e => set('status', e.target.value)}
                style={{ ...inputStyle, appearance: 'none', paddingRight: 36 }}
              >
                {['pending', 'scheduled', 'picked_up', 'completed', 'cancelled'].map(s => (
                  <option key={s} value={s}>
                    {s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </option>
                ))}
              </select>
              <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#aaa' }}>▾</span>
            </div>
          </div>

          {/* ── City + Address ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>📍 City</label>
              <input
                type="text"
                value={form.city}
                onChange={e => set('city', e.target.value)}
                placeholder="e.g. Indore"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>🏠 Address</label>
              <input
                type="text"
                value={form.address}
                onChange={e => set('address', e.target.value)}
                placeholder="Full pickup address"
                style={inputStyle}
              />
            </div>
          </div>

          {/* ── Date + Time Slot ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 8 }}>
            <div>
              <label style={labelStyle}>📅 Preferred Date</label>
              <input
                type="date"
                value={form.preferred_date}
                min={todayStr}
                onChange={e => set('preferred_date', e.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>🕐 Time Slot</label>
              <div style={{ position: 'relative' }}>
                <select
                  value={form.preferred_slot}
                  onChange={e => set('preferred_slot', e.target.value)}
                  style={{ ...inputStyle, appearance: 'none', paddingRight: 36 }}
                >
                  <option value="">— Select slot —</option>
                  {TIME_SLOTS.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#aaa' }}>▾</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn-modal-cancel" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="btn-modal-submit"
            onClick={handleSubmit}
            disabled={submitting || !form.assigned_driver_id}
          >
            {submitting ? '⏳ Saving...' : '✅ Save & Assign'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const PickupRequestsManagement = () => {
  const [pickups, setPickups]             = useState([]);
  const [drivers, setDrivers]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [searchTerm, setSearchTerm]       = useState('');
  const [filterStatus, setFilterStatus]   = useState('all');
  const [modalPickup, setModalPickup]     = useState(null);
  const [updating, setUpdating]           = useState({});

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [pickupRes, driverRes] = await Promise.all([
        adminLogisticsService.getPickupRequests(),
        adminLogisticsService.getDrivers(),
      ]);
      setPickups(Array.isArray(pickupRes) ? pickupRes : []);
      setDrivers(Array.isArray(driverRes) ? driverRes : []);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (pickupId, newStatus) => {
    try {
      setUpdating(u => ({ ...u, [pickupId]: true }));
      await adminLogisticsService.updatePickupRequest(pickupId, { status: newStatus });
      loadAll();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setUpdating(u => ({ ...u, [pickupId]: false }));
    }
  };

  const statuses = ['all', 'pending', 'scheduled', 'picked_up', 'completed', 'cancelled'];

  const filtered = pickups.filter(p => {
    const statusMatch = filterStatus === 'all' || p.status === filterStatus;
    const s = searchTerm.toLowerCase();
    const searchMatch = !s ||
      p.id?.toString().includes(s) ||
      p.sell_request_id?.toString().includes(s) ||
      p.city?.toLowerCase().includes(s) ||
      p.address?.toLowerCase().includes(s);
    return statusMatch && searchMatch;
  });

  const fmt = d => d
    ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : 'N/A';

  const driverName = id => {
    const d = drivers.find(dr => String(dr.id) === String(id));
    return d ? d.name : `ID: ${id}`;
  };

  if (loading) return (
    <div className="management-loading">
      <div className="spinner-large" />
      <p>Loading pickup requests...</p>
    </div>
  );

  return (
    <div className="inspections-management">
      {/* Header */}
      <div className="management-header">
        <div>
          <h1>🚚 Pickup Requests</h1>
          <p>Assign drivers and schedule pickup details</p>
        </div>
        <button className="refresh-btn" onClick={loadAll}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M21.5 2V8M21.5 8H15.5M21.5 8L18.5 5C17.2 3.7 15.4 2.8 13.5 2.3C9.5 1.3 5.5 2.9 3.5 6.3C1.5 9.7 1.8 14 4.5 17M2.5 22V16M2.5 16H8.5M2.5 16L5.5 19C6.8 20.3 8.6 21.2 10.5 21.7C14.5 22.7 18.5 21.1 20.5 17.7C22.5 14.3 22.2 10 19.5 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="management-filters">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
            <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            placeholder="Search by ID, sell request, city, address..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-tabs">
          {statuses.map(status => (
            <button
              key={status}
              className={`filter-tab ${filterStatus === status ? 'active' : ''}`}
              onClick={() => setFilterStatus(status)}
            >
              {status === 'all' ? 'All' : status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              <span className="filter-count">
                {status === 'all' ? pickups.length : pickups.filter(p => p.status === status).length}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="management-table-wrapper" style={{ overflowX: 'auto' }}>
        <div className="management-table">
          <table style={{ tableLayout: 'auto', width: '100%', minWidth: '700px' }}>
            <thead>
              <tr>
                <th style={{ whiteSpace: 'nowrap' }}>ID</th>
                <th style={{ whiteSpace: 'nowrap' }}>Sell Req.</th>
                <th>City</th>
                <th>Address</th>
                <th style={{ whiteSpace: 'nowrap' }}>Date</th>
                <th style={{ whiteSpace: 'nowrap' }}>Slot</th>
                <th>Driver</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(pickup => {
                const isUpdating = updating[pickup.id];
                return (
                  <tr key={pickup.id}>
                    <td style={{ whiteSpace: 'nowrap' }}><strong>#{pickup.id}</strong></td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <code style={{ fontSize: '11px' }}>#{pickup.sell_request_id}</code>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <p style={{ fontSize: '12px', margin: 0 }}>📍 {pickup.city || '—'}</p>
                    </td>
                    <td>
                      <p style={{ fontSize: '11px', margin: 0, color: pickup.address ? '#222' : '#bbb' }}>
                        {pickup.address || 'Not set'}
                      </p>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <p style={{ fontSize: '11px', margin: 0 }}>{fmt(pickup.preferred_date)}</p>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <p style={{ fontSize: '11px', margin: 0 }}>{pickup.preferred_slot || '—'}</p>
                    </td>
                    <td>
                      {pickup.assigned_driver_id ? (
                        <div>
                          <p style={{ fontWeight: 700, color: '#28a745', fontSize: '12px', margin: 0 }}>
                            {driverName(pickup.assigned_driver_id)}
                          </p>
                          <small style={{ color: '#999', fontSize: '10px' }}>ID: {pickup.assigned_driver_id}</small>
                        </div>
                      ) : (
                        <span style={{ color: '#dc3545', fontSize: '11px', whiteSpace: 'nowrap' }}>Not Assigned</span>
                      )}
                    </td>
                    <td><StatusBadge status={pickup.status} /></td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-approve"
                          onClick={() => setModalPickup(pickup)}
                          title="Edit / Assign Driver"
                        >
                          👤
                        </button>
                        {pickup.status === 'scheduled' && (
                          <button
                            className="btn-approve"
                            disabled={isUpdating}
                            onClick={() => handleStatusChange(pickup.id, 'picked_up')}
                            title="Mark Picked Up"
                            style={{ background: '#6f42c120', color: '#6f42c1' }}
                          >
                            📦
                          </button>
                        )}
                        {pickup.status === 'picked_up' && (
                          <button
                            className="btn-approve"
                            disabled={isUpdating}
                            onClick={() => handleStatusChange(pickup.id, 'completed')}
                            title="Mark Completed"
                            style={{ background: '#28a74520', color: '#28a745' }}
                          >
                            ✅
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="no-results"><p>No pickup requests found</p></div>
          )}
        </div>
      </div>

      {/* Modal */}
      {modalPickup && (
        <AssignDriverModal
          pickup={modalPickup}
          drivers={drivers}
          onClose={() => setModalPickup(null)}
          onSuccess={() => { setModalPickup(null); loadAll(); }}
        />
      )}
    </div>
  );
};

export default PickupRequestsManagement;